import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';
import Highlighter from 'react-highlighter';

import { ChevronUpSmallIcon, SearchIcon } from '@/assets/icons';
import { Input } from '@/components/atoms';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { cn } from '@/utils/tailwind.ts';

import { Button } from '../../../atoms';
import {
  Dropdown,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';

export type LeaderDropdownOption = {
  label: {
    fullName: string;
    jobTitle: string;
  };
  value: string;
  image?: string | null;
  [key: string]: any;
};

const getLeaderInitials = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  const first = parts[0][0] ?? '';
  const last = parts[parts.length - 1][0] ?? '';
  return `${first}${last}`.toUpperCase();
};

type LeaderAvatarProps = {
  fullName: string;
  imageUrl?: string | null;
  size: number;
  textClassName?: string;
};

const LeaderAvatar: FC<LeaderAvatarProps> = ({ fullName, imageUrl, size, textClassName }) => {
  const trimmed = imageUrl?.trim() ?? '';
  const [showImage, setShowImage] = useState(Boolean(trimmed));

  useEffect(() => {
    setShowImage(Boolean(trimmed));
  }, [trimmed]);

  if (showImage && trimmed) {
    return (
      <img
        src={trimmed}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover"
        onError={() => setShowImage(false)}
      />
    );
  }

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-custom-warm-grey-200 font-semibold text-custom-warm-grey-700',
        textClassName
      )}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)) }}
      aria-hidden
    >
      {getLeaderInitials(fullName)}
    </span>
  );
};

type LeaderDropdownProps = {
  value: string | null | undefined;
  options: LeaderDropdownOption[];
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: 'default' | 'transparent';
  className?: string;
  filterValue?: string;
  selectedLabel?: string;
  placeholder?: string;
  onClickClearSeletion?: () => void;
  onFilterChange?: (value: string) => void;
  onLoadMore?: () => void;
  onChange: (newValue: LeaderDropdownOption | null) => void;
};

export const LeaderDropdown: FC<LeaderDropdownProps> = ({
  value,
  options,
  triggerLabel,
  triggerIcon,
  triggerVariant = 'default',
  className,
  filterValue,
  selectedLabel,
  placeholder,
  onClickClearSeletion,
  onFilterChange,
  onLoadMore,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { setObservedNode } = useIntersectionObserver({
    isEnabled: Boolean(isOpen && onLoadMore),
    onIntersect: () => onLoadMore?.(),
    rootMargin: '0px 0px 120px 0px',
    reobserveKey: options.length,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFilterChange?.(e.target.value);
  };

  const handleSelect = (option: LeaderDropdownOption | null) => {
    const isExist = selectedOption?.value === option?.value;
    onChange(isExist ? null : option);
  };

  const hasSelection = Boolean(value);

  const selectedOption = options.find(option => option.value === value);
  const selectedOptionLabel = selectedOption?.label.fullName ?? selectedLabel;

  return (
    <Dropdown onOpenChange={setIsOpen}>
      <DropdownMenuTrigger variant={triggerVariant} asChild>
        <Button variant="secondary">
          <div className="flex items-center gap-2 text-custom-warm-grey-500">
            {hasSelection ? (
              <div
                className={cn(
                  'transition-colors flex items-center gap-2',
                  isOpen ? 'text-custom-warm-grey-700' : 'text-custom-warm-grey-500'
                )}
              >
                <LeaderAvatar
                  fullName={selectedOptionLabel ?? ''}
                  imageUrl={selectedOption?.image}
                  size={20}
                />
                {selectedOptionLabel}
              </div>
            ) : (
              <>
                {triggerIcon}{' '}
                <span
                  className={cn(
                    'transition-colors',
                    isOpen ? 'text-custom-warm-grey-700' : 'text-custom-warm-grey-500'
                  )}
                >
                  {triggerLabel}
                </span>
              </>
            )}
            <span className="text-custom-warm-grey-400">
              <ChevronUpSmallIcon
                className={cn(
                  'transition-transform text-custom-warm-grey-400',
                  isOpen ? 'rotate-180' : 'rotate-0'
                )}
              />
            </span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn('flex max-h-[600px] flex-col', className)} align="start">
        <div className="sticky top-0 z-10 bg-white">
          <Input
            leftIcon={<SearchIcon />}
            value={filterValue}
            onChange={handleChange}
            placeholder={placeholder}
            className="select-none"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-color:theme(colors.custom-warm-grey-100)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[16px] [&::-webkit-scrollbar-thumb]:bg-custom-warm-grey-100">
          {options.map(option => (
            <DropdownMenuItem
              key={option.value}
              checked={option.value == selectedOption?.value}
              onCheckedChange={() => handleSelect(option)}
            >
              <div className="flex items-center gap-2">
                <div>
                  <LeaderAvatar
                    fullName={option.label.fullName}
                    imageUrl={option.image}
                    size={32}
                  />
                </div>
                <div>
                  <p className="text-body-14-semibold mb-1">
                    <Highlighter search={filterValue ?? ''}>{option.label.fullName}</Highlighter>
                  </p>
                  <p className="text-body-12-regular text-custom-warm-grey-600">
                    {option.label.jobTitle}
                  </p>
                </div>
              </div>
            </DropdownMenuItem>
          ))}
          <div ref={setObservedNode} className="h-px w-full" />
        </div>
        {hasSelection && (
          <div className="sticky bottom-0 z-10 bg-white">
            <DropdownMenuSeparator />
            <DropdownMenuClearSelection onClick={onClickClearSeletion}>
              Clear selection
            </DropdownMenuClearSelection>
          </div>
        )}
      </DropdownMenuContent>
    </Dropdown>
  );
};
